import React, { useEffect, useState } from "react";
import axios from "axios";
import { Pencil, Trash2, Search } from "lucide-react";
import AdminLayout from "./adminLayout";

const API_URL = process.env.REACT_APP_API_URL;

const ProductManager = () => {
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/products/getallproducts`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      setProducts(response.data);

      // Extract unique categories
      const allCategories = response.data.flatMap(product => product.category || []);
      const uniqueCategories = [...new Set(allCategories.filter(Boolean))];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const searchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/products/search`, {
        params: { query: searchQuery },
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      setProducts(response.data);
    } catch (error) {
      console.error("Error searching products:", error);
      if (error.response?.status === 404) {
        setProducts([]);
      }
    }
  };

  const filterByCategory = async (selectedCategory) => {
    try {
      if (!selectedCategory) {
        fetchProducts();
        return;
      }

      const response = await axios.get(`${API_URL}/api/products/category/${selectedCategory}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")} `}
      });
      setProducts(response.data);
    } catch (error) {
      console.error("Error filtering products:", error);
      if (error.response?.status === 404) {
        setProducts([]);
      }
    }
  };

  const deleteProduct = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/products/delete/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      fetchProducts();
    } catch (error) {
      console.error("Error deleting product:", error);
      alert(error.response?.data || "Error deleting product");
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;

    // Special handling for category (stored as array)
    if (name === "category") {
      setEditingProduct({ ...editingProduct, category: value.split(",").map(c => c.trim()) });
    } else {
      setEditingProduct({ ...editingProduct, [name]: value });
    }
  };

  const updateProduct = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${API_URL}/api/products/update/${editingProduct.id || editingProduct.id}`,
        editingProduct,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")} `},
        }
      );
      fetchProducts();
      setEditingProduct(null);
      setShowEditForm(false);
      alert("Product updated successfully");
    } catch (error) {
      console.error("Error updating product:", error);
      alert(error.response?.data || "Error updating product");
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  return (
    <AdminLayout>
      <div className="p-6 mt-12">
        <h2 className="text-2xl font-bold mb-6">Manage Products</h2>

        <div className="mb-6 flex flex-col md:flex-row gap-4">
          <div className="flex w-full md:w-1/2">
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full border rounded-l p-2"
            />
            <button
              onClick={searchProducts}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 rounded-r flex items-center"
            >
              <Search size={20} />
            </button>
          </div>

          <select 
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              filterByCategory(e.target.value);
            }}
            className="border rounded p-2 w-full md:w-1/3"
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        {products.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((product) => (
              <div
                key={product.id}
                className="bg-white shadow-md p-4 rounded-lg"
              >
                <img
                  src={
                    product.image 
                      ? (product.image.startsWith("http") 
                          ? product.image 
                          : `${API_URL}${product.image}`)
                      : "https://via.placeholder.com/150"
                  }
                  alt={product.title}
                  className="w-full h-40 object-cover rounded mb-4"
                />

                <h3 className="text-m font-semibold mb-2">{product.title}</h3>
                {product.categories && (
                  <p className="text-sm text-gray-600 mb-2">
                    Category: {Array.isArray(product.categories) ? product.categories.join(", ") : product.categories}
                  </p>
                )}
                <p className="text-sm text-gray-500">Stock: {product.stock}</p>
                <div className="flex justify-between items-center mt-2">
                  <p className="font-bold text-m text-gray-800">
                    ₹{product.price}
                  </p>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        setEditingProduct(product);
                        setShowEditForm(true);
                      }}
                      className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-full transition-transform duration-200 hover:scale-105 shadow"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => deleteProduct(product.id)}
                      className="flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-full transition-transform duration-200 hover:scale-105 shadow"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-600">No products found</p>
          </div>
        )}

        {showEditForm && editingProduct && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
            <form
              onSubmit={updateProduct}
              className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md"
            >
              <h2 className="text-xl font-semibold mb-4">Edit Product</h2>
              <input
                type="text"
                name="title"
                value={editingProduct.title || ""}
                onChange={handleEditChange}
                placeholder="Product Title"
                className="w-full border p-2 mb-3 rounded"
                required
              />
              <input
                type="number"
                name="price"
                value={editingProduct.price || ""}
                onChange={handleEditChange}
                placeholder="Price"
                className="w-full border p-2 mb-3 rounded"
                required
              />
              <input
                type="text"
                name="category"
                value={editingProduct.categories?.join(", ") || ""}
                onChange={handleEditChange}
                placeholder="Categories (comma-separated)"
                className="w-full border p-2 mb-3 rounded"
                required
              />
              <input
                type="number"
                name="stock"
                value={editingProduct.stock || ""}
                onChange={handleEditChange}
                placeholder="Stock"
                className="w-full border p-2 mb-3 rounded"
                required
              />
              <input
                type="text"
                name="image"
                value={editingProduct.image || ""}
                onChange={handleEditChange}
                placeholder="Image URL"
                className="w-full border p-2 mb-3 rounded"
                required
              />
              <textarea
                name="description"
                value={editingProduct.description || ""}
                onChange={handleEditChange}
                placeholder="Product Description"
                className="w-full border p-2 mb-3 rounded"
                required
              />

              <div className="flex justify-between mt-4">
                <button
                  type="submit"
                  className="bg-green-600 hover:bg-green-700 text-white font-semibold px-5 py-2 rounded-lg shadow-md transition-transform duration-200 hover:scale-105"
                >
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditForm(false);
                    setEditingProduct(null);
                  }}
                  className="bg-gray-500 hover:bg-gray-600 text-white font-semibold px-5 py-2 rounded-lg shadow-md transition-transform duration-200 hover:scale-105"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default ProductManager;
